import TopNavigationBar from "pureUi/TopNavigationBar";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import * as AuthSelectors from 'store/modules/auth';
import * as BookingManagerSelectors from 'store/modules/bookingManager/selectors';
import * as BreakdownSelectors from 'store/modules/bookingManager/subdomains/breakdown/selectors';
import * as DashboardActions from 'store/modules/bookingManager/subdomains/dashboard/actions';
import { bookingInformationReservationTeamDashboardSelector, dashboardNetworkRequestsSelector, topNavigationDataDashboardSelector } from "store/modules/bookingManager/subdomains/dashboard/selectors";
import { ENetworkRequestStatus, EUserType } from "services/BackendApi";
import ProductAllocationMatrix from "./ProductAllocationMatrix";
import { useAllocationMatrixQuery } from "./useAllocationMatrixQuery";
import { makeBookingManagerApi } from "services/BookingManagerApi";
import { LoadingBar } from "ui/NetworkStatusBar";
import { useTabFocus } from "hooks/useTabFocus";
import { VerticalSpace } from "ui/VerticalSpace";
import { GuestAllocationFilter } from "./GuestAllocationFilter";
import { createAllocationKey, parseAllocationKey } from "./utils";
import { LeaveWithoutSavingModal } from "ui/LeaveWithoutSavingModal";
import { isEqual } from "lodash-es";
import { Warning } from "ui/Warning";
import { SvgIcon } from "ui/SvgIcon";
import { closeBold } from "ui/Icons";
import { formatDateDisplay } from "utils";
import { AllocationsItem, VersionsList } from "services/BookingManagerApi/types/AllocationsResponse";
import { Select } from "ui/Select";

export type AllocationMap = Record<string, boolean>;

export const BookingManagerGuestAllocation: React.FC = () => {
  const { bookingUuid } = useParams<{ bookingUuid: string }>();
  const [filteredGuestIds, setFilteredGuestIds] = useState<string[]>([]);
  const [localAllocations, setLocalAllocations] = useState<AllocationMap>({});
  const [productsWithoutAssignment, setProductsWithoutAssignment] = useState<string[]>([]);
  const [activeVersion, setActiveVersion] = useState<VersionsList | null | undefined>(undefined);

  const userRole = useSelector(AuthSelectors.getCurrentUserType) as EUserType;
  const topNavigationData = useSelector(topNavigationDataDashboardSelector);
  const bookingInformationReservationTeamData = useSelector(bookingInformationReservationTeamDashboardSelector);
  const dashboardNetworkRequests = useSelector(dashboardNetworkRequestsSelector);
  const headlineLineItemBreakdown = useSelector(BreakdownSelectors.headlineLineItemBreakdownSelector);
  const booking = useSelector(BookingManagerSelectors.bookingSelector);

  const { getAllocationMatrix, patchAllocationMatrix } = useAllocationMatrixQuery(makeBookingManagerApi(), booking?.bookingUuid, activeVersion?.timestamp);
  const dispatch = useDispatch();

  const allocations = getAllocationMatrix.data?.data.data.allocations || [];
  const guests = getAllocationMatrix.data?.data.data.guests || [];
  const products = getAllocationMatrix.data?.data.data.breakdownItems || [];
  const versionList = getAllocationMatrix.data?.data.versions || [];
  const filteredGuests = guests.filter(g => filteredGuestIds.includes(g.uuid));

  const allocationItems: AllocationsItem[] = useMemo(() =>
    Object.entries(localAllocations)
      .map(([key, value]) => {
        if (value) {
          return parseAllocationKey(key);
        }
      })
      .filter((val) => val !== undefined) as AllocationsItem[]
    , [localAllocations, filteredGuests]);

  const isLatestVersion = useMemo(() => {
    if (!activeVersion || !versionList.length) {
      return true;
    }
    return activeVersion?.version === versionList[0].version;
  }, [activeVersion, versionList]);

  const disabled = useMemo(() => {
    return !(
      isLatestVersion && [EUserType.TA, EUserType.SR, EUserType.ADMIN].includes(userRole)
    );
  }, [isLatestVersion, userRole]);

  const handlePatchAllocationMatrix = useCallback(() => {
    setActiveVersion(null);
    patchAllocationMatrix.mutate(allocationItems);
  }, [allocationItems, patchAllocationMatrix]);

  useEffect(() => {
    const initialAllocations: AllocationMap = {};
    const allocationProducts = Object.fromEntries(products.map(product => [product.uuid, { hasAllocation: false, name: product.name }]));
    allocations.forEach(item => {
      const key = createAllocationKey(item.breakdownItemUuid, item.guestUuid);
      if (allocationProducts[item.breakdownItemUuid]) {
        allocationProducts[item.breakdownItemUuid].hasAllocation = true;
      }
      initialAllocations[key] = true;
    });
    setLocalAllocations(initialAllocations);
    setProductsWithoutAssignment(
      Object.keys(allocationProducts)
        .filter(product => !allocationProducts[product].hasAllocation)
        .map(product => allocationProducts[product].name)
    );
  }, [getAllocationMatrix.data?.data.data]);

  useEffect(() => {
    if (guests.length) {
      setFilteredGuestIds(guests.map(g => g.uuid));
    }
  }, [guests]);

  useEffect(() => {
    dispatch(DashboardActions.getTopNavigationDataRequestAction(bookingUuid));
  }, [bookingUuid]);

  useTabFocus(() => {
    isLatestVersion && getAllocationMatrix.refetch();
  }, 3000);

  if (getAllocationMatrix.isFetching) {
    return <LoadingBar />;
  }

  return (
    <>
      <TopNavigationBar
        userRole={userRole as EUserType}
        data={topNavigationData}
        headlineLineItemBreakdown={headlineLineItemBreakdown}
        bookingInformationReservationTeamData={bookingInformationReservationTeamData}
        isError={dashboardNetworkRequests.topNavigationDataLoad === ENetworkRequestStatus.ERROR}
      />
      <div className="guest-allocation relative">
        <div className="flex items-center justify-between">
          <h2 className="font-noe-display text-4xl text-black leading-42px font-normal mt-0 mb-0">Guest Allocation</h2>
          <div className="flex flex-row items-center justify-end mb-4">
            {versionList[0]?.timestamp && (
              <span className="last-modified self-center">
                Date last changed: {formatDateDisplay(new Date(parseInt(versionList[0].timestamp, 10)))}
              </span>
            )}

            <Select
              className="version-selector min-w-170px max-w-255px pl-10px pr-45px ml-4 text-ellipsis normal-case"
              value={activeVersion ? activeVersion.version.toString() : null}
              onChange={event => {
                const selectedVersionId = versionList.findIndex(
                  v => v.version === parseInt(event.target.value)
                );
                setActiveVersion(selectedVersionId > 0 ? versionList[selectedVersionId] : null);
              }}
              options={versionList.map(version => {
                const v = `V${version.version}, ${formatDateDisplay(new Date(parseInt(version.timestamp, 10)))}`;
                const u = version.user?.email;
                return {
                  value: String(version.version),
                  label: [v, u].filter(Boolean).join(' / '),
                };
              })}
            />
          </div>
        </div>
        {patchAllocationMatrix.isSuccess && productsWithoutAssignment.length > 0 && isLatestVersion && (
          <>
            <VerticalSpace height="20px" />
            <Warning message="Check missing requirements." description={
              <div className="flex flex-col gap-5px mt-10px pl-3px">
                {productsWithoutAssignment.map(product =>
                  <div className="flex gap-5px text-red-95 text-13px">
                    <SvgIcon IconComponent={closeBold} className="fill-red-95 w-11px flex items-center" />'{product}': Pending to allocate guest.
                  </div>)
                }
              </div>
            } />
          </>
        )}
        <VerticalSpace height="20px" />
        <GuestAllocationFilter
          isLatestVersion={isLatestVersion}
          filteredGuestIds={filteredGuestIds}
          guests={guests}
          setFilteredGuestIds={setFilteredGuestIds}
        />
        <VerticalSpace height="20px" />
        <ProductAllocationMatrix
          guests={filteredGuests}
          products={products}
          allocations={localAllocations}
          setAllocations={setLocalAllocations}
          handlePatchAllocationMatrix={handlePatchAllocationMatrix}
          isPatchAllocationPending={patchAllocationMatrix.isPending}
          isPatchAllocationDisabled={isEqual(allocationItems, allocations)}
          disabled={disabled}
        />
      </div>
      <LeaveWithoutSavingModal
        title="You have made changes to this page, and not saved. If you leave this page now, these changes will be lost."
        when={!isEqual(allocationItems, allocations)}
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
      />
    </>
  );
}